import React, { useState } from "react";
import { format, parseISO } from "date-fns";
import { nl } from "date-fns/locale";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2, Calendar, Clock, User } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePackagePickupLogs, useDeletePackagePickupLog } from "@/hooks/usePackagePickupQueries";
import PackagePickupForm from "./PackagePickupForm";
import { PackagePickupLog } from "@/types/packagePickup";
import { usePermissions } from "@/context/PermissionsContext";

const PackagePickupList = () => {
  const [editingLog, setEditingLog] = useState<PackagePickupLog | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteLog, setDeleteLog] = useState<PackagePickupLog | null>(null);
  
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("packagePickup", "write");
  const canDelete = hasPermission("packagePickup", "delete");

  // Get last 30 days of logs
  const endDate = format(new Date(), "yyyy-MM-dd");
  const startDate = format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), "yyyy-MM-dd");
  
  const { data: logs = [], isLoading } = usePackagePickupLogs(startDate, endDate);
  const deleteMutation = useDeletePackagePickupLog();

  const handleEdit = (log: PackagePickupLog) => {
    setEditingLog(log);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleteLog) return;
    
    try {
      await deleteMutation.mutateAsync(deleteLog.id);
      setDeleteLog(null);
    } catch (error) {
      console.error("Delete error:", error);
      // Error is already handled in the mutation onError callback
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return format(parseISO(dateStr), "EEEE d MMMM yyyy", { locale: nl });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr: string) => {
    return timeStr.slice(0, 5); // Remove seconds if present
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="h-12 bg-muted rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Nog geen ophaaltijden geregistreerd</p>
        <p className="text-sm">Begin met het registreren van je eerste ophaaltijd</p>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[200px]">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Datum
                </div>
              </TableHead>
              <TableHead>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Tijd
                </div>
              </TableHead>
              <TableHead>Rolcontainers</TableHead>
              <TableHead>Notities</TableHead>
              <TableHead>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Geregistreerd door
                </div>
              </TableHead>
              <TableHead className="text-right">Acties</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>
                  <div className="capitalize">
                    {formatDate(log.pickup_date)}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-mono">
                    {formatTime(log.pickup_time)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge variant="secondary">
                    {log.aantal_rolcontainers}
                  </Badge>
                </TableCell>
                <TableCell>
                  {log.notes ? (
                    <div className="max-w-xs truncate" title={log.notes}>
                      {log.notes}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm">
                    {log.registered_by_name}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {canWrite && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(log)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteLog(log)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <PackagePickupForm
        open={showForm}
        onOpenChange={(open) => {
          setShowForm(open);
          if (!open) setEditingLog(null);
        }}
        editingLog={editingLog}
      />

      <AlertDialog open={!!deleteLog} onOpenChange={() => setDeleteLog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ophaaltijd verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je de ophaaltijd van{" "}
              {deleteLog && formatDate(deleteLog.pickup_date)} om{" "}
              {deleteLog && formatTime(deleteLog.pickup_time)} wilt verwijderen?
              Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Bezig..." : "Verwijderen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default PackagePickupList;